import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'angular-toastify';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { Action } from 'src/app/core/models/action.model';
import { DynamicService } from 'src/app/core/services/base/dynamic.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SupabaseService } from 'src/app/core/services/base/supabase.service';
import { GuideService } from 'src/app/modules/client/pages/guide/services/guide.service';
import { ConfirmModalComponent } from 'src/app/shared/components/simple/confirm-modal/confirm-modal.component';
import { DownloadDocumentComponent } from '../../../../simple/os-action-item/modals/download-document/download-document.component';
import { PendingSignatureComponent } from '../../../../simple/os-action-item/modals/pending-signature/pending-signature.component';
import { EletronicSignatureModalComponent } from '../../../electronic-signature/components/eletronic-signature-modal/eletronic-signature-modal.component';
import { OaSignatureService } from './service/oa-signature.service';
import { environment } from 'src/environments/environment';
import { AgentClientsService } from 'src/app/core/services/agent-clients.service';
import { DeferCaseActionService } from 'src/app/core/services/defer-case-action.service';
import { UserService } from 'src/app/core/services/user.service';
import { buildPrereqTooltip } from 'src/app/core/utils/prereq-tooltip.util';

@Component({
  selector: 'app-oa-signature',
  templateUrl: './oa-signature.component.html',
  styleUrls: ['./oa-signature.component.scss', '../oa-styles.scss'],
})
export class OaSignatureComponent implements OnDestroy {
  @Input() action!: Action;
  @Output() changed = new EventEmitter<boolean>();

  infoPendingMessage = 'This action is pending. Please wait.';

  isLoading = false;

  deferring = false;
  deferred = false;

  destroySubject = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private ds: DynamicService<any>,
    private service: OaSignatureService,
    private ts: ToastService,
    private translate: TranslateService,
    private http: HttpClient,
    private supabaseService: SupabaseService,
    private guideService: GuideService,
    private router: Router,
    private agentClientsService: AgentClientsService,
    private deferCaseActionService: DeferCaseActionService,
    private userService: UserService,
  ) {}

  ngOnInit() {
    console.log('[OA SIGNATURE] ngOnInit', this.action);

    const deferredClaims = this.action.case_action_config?.simple_questionnaire?.deferred_claims;
    this.deferred = Array.isArray(deferredClaims) && !!this.action.claim_id && deferredClaims.includes(this.action.claim_id);
    // console.log('[OA SIGNATURE] prstatus:', this.action.prstatus);
    // console.log('[OA SIGNATURE] ac_status:', this.action.ac_status);
    // console.log('[OA SIGNATURE] checkStatus():', this.checkStatus());
    
    // this.signatureDialog();
    // get case config

    this.ds.load('cpv_actions_complete');
    this.ds
      .autofillQuery('cp_case_actions', '*', [
        { op: 'eq', col: 'action_id', val: this.action.action_id! },
      ])
      .then((data) => {
        if (data.length > 0) {
          this.infoPendingMessage = (data[0] as any).config.info_pending;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  async signatureDialog() {
    // ✅ CPK-3911: se o claim desta action ainda exige o guide, redireciona
    // para lá em vez de abrir o modal de assinatura fora do fluxo do guide.
    if (this.action.claim_id) {
      const requiresGuide = await this.guideService.claimRequiresGuide(
        this.action.claim_id
      );

      if (requiresGuide) {
        console.log('[OA] [SIGNATURE] Claim still requires guide, redirecting', {
          claimId: this.action.claim_id,
        });
        this.router.navigateByUrl(`client/claim/${this.action.claim_id}/guide`);
        return;
      }
    }

    // If No signature or failed, display signing pad.
    let dialogRef;
    if (this.action.ac_status !== 'DONE') {
      dialogRef = this.dialog.open(EletronicSignatureModalComponent, {
        data: {
          contract_id: this.action.contract_id,
          claim_id: this.action.claim_id,
        },
        width: 'min(90vw, 720px)',
      });
      dialogRef.afterClosed().subscribe((result) => {
        console.log(`Dialog result: ${result}`);
        this.changed.emit(true);
      });
    } else if ((this.action.ac_status as any) === 'SIGNED' || (this.action.ac_status as any) === 'REPLACED') {
      // ✅ SIGNED ou REPLACED = contrato assinado, display popup with download option
      dialogRef = this.dialog.open(DownloadDocumentComponent, {
        data: {
          signed_document_id: this.action.done_action_id,
        },
      });
    } else {
      // If there is a signature, but its pending, display please wait.
      dialogRef = this.dialog.open(PendingSignatureComponent, {
        data: {
          signed_document_id: this.action.done_action_id,
        },
      });
    }
  }

  checkDependsOn() {
    return (this.action.prstatus as any) === 'DONE';
  }

  checkStatus() {
    let status = 'OPEN';

    // ✅ SIGNED = versão atual assinada, REPLACED = versão anterior foi assinada (contrato atualizado)
    if ((this.action.ac_status as any) === 'SIGNED') {
      // console.log('[OA SIGNATURE] checkStatus - DONE (SIGNED or REPLACED)');
      return 'DONE';
    }

    if (this.action.ac_status === 'PENDING') {
      // console.log('[OA SIGNATURE] checkStatus - PENDING');
      status = 'PENDING';
    }

    // se o ac_status for diferente de 'DONE' mas o prstatus for igual a 'SUBMITTED' esta em processamento
    if (
      (this.action.ac_status as any) !== 'DONE' &&
      (this.action.prstatus as any) === 'OPEN'
    ) {
      // console.log('[OA SIGNATURE] checkStatus - BLOCKED (prstatus OPEN)');
      status = 'BLOCKED';
    }

    // console.log('[OA SIGNATURE] checkStatus - returning:', status);
    return status;
  }

  async downloadDocument() {
    this.isLoading = true;
    try {
      const res = await this.service.getAllGroupedByCase(
        this.action.claim_id!,
        this.action.contract_id ?? undefined,
      );

      if (!res?.id) {
        this._showErrorSnackbar('Error on download document');
        return;
      }

      const session = await this.supabaseService.supabase.auth.getSession();
      const token = session.data?.session?.access_token;
      if (!token) return;

      const lambdaUrl = environment.lambdaUrl.replace(/\/$/, '');
      const url = `${lambdaUrl}/download-signed-documents?signed_contract_id=${res.id}&action=download`;

      const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
      const blob = await firstValueFrom(
        this.http.get(url, { headers, responseType: 'blob' })
      );

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = (this.action?.action_name || 'Contract') + '.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error(err);
      this._showErrorSnackbar('Error on download document');
    } finally {
      this.isLoading = false;
    }
  }

  retractSignature() {
    this.isLoading = true;

    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          message: 'Are you sure you want to retract your signature?',
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroySubject))
      .subscribe(async (result) => {
        try {
          if (result) {
            const res = await this.service.getAllGroupedByCase(
              this.action.claim_id!,
              this.action.contract_id ?? undefined // ✅ Passa contract_id para verificar versões anteriores
            );
            if (res) {
              await this.service.retract(res.id);
              this.changed.emit(true);
            } else {
              throw 'without signature';
            }
          }
        } catch (err) {
          console.error(err);
          this._showErrorSnackbar('Error on retract signature');
        } finally {
          this.isLoading = false;
        }
      });
  }

  private _showErrorSnackbar(message: string) {
    const translatedMessage = this.translate.instant(message);
    this.ts.error(translatedMessage);
  }

  get showDeferButton(): boolean {
    // CPK-4864: depois de adiada, a action some do "Continuar depois" e fica
    // só com a tag to-do — o cliente retoma pelo próprio card.
    return this.agentClientsService.isSimpleQuestionnaireSession() && !this.deferred && this.checkStatus() !== 'DONE';
  }

  /** CPK-4864: tooltip de bloqueio com o nome das actions pré-requisito pendentes. */
  get blockedTooltip(): string {
    return buildPrereqTooltip(this.action.blocking_prereq_names, this.translate);
  }

  async continueLater(): Promise<void> {
    if (this.deferring || this.deferred) return;

    const uid = (await this.userService.getCurrentUser())?.id;
    if (!uid || !this.action.case_action_id || !this.action.claim_id) return;

    this.deferring = true;
    try {
      await this.deferCaseActionService.defer(this.action.case_action_id, uid, this.action.claim_id);
      this.deferred = true;
      console.log('[OA SIGNATURE] Deferred (CPK-4745)', { caseActionId: this.action.case_action_id });
    } catch (error) {
      console.error('[OA SIGNATURE] Error deferring action', error);
      this.ts.error(this.translate.instant('DeferCaseActionError'));
    } finally {
      this.deferring = false;
    }
  }
}
